'use client';

import clsx from 'clsx';
import { motion } from 'motion/react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { ButtonLoader } from '../forms/buttons';

export interface ISelectDropdownOption {
  /**
   * The name of the option.
   */
  label: string;
  /**
   * The value of the option.
   */
  value: string;
}

/**
 * Properties for the SelectDropdown component.
 */
interface ISelectDropdownProps {
  /** Whether the dropdown is disabled. */
  disabled?: boolean;
  /** Error message to display below the dropdown. */
  error?: string;
  /** Whether the label is enabled and visible. */
  isLabelEnabled?: boolean;
  /** Whether the dropdown is in a loading state. */
  isLoading?: boolean;
  /** The label text for the dropdown. */
  label?: string;
  /** The name of the dropdown field. */
  name: string;
  /** Array of options to display in the dropdown. */
  options: ISelectDropdownOption[];
  /** Callback for the blur event. */
  onBlur?: (name: string, selected: ISelectDropdownOption) => void;
  /** Callback triggered when the selected option changes. */
  onChange: (name: string, selected: ISelectDropdownOption) => void;
  /** The currently selected option. */
  selected: ISelectDropdownOption;
}

/**
 * -----------------------------------------------------------------------------
 * It renders the select dropdown.
 */
export function SelectDropdown({
  disabled,
  error,
  isLabelEnabled,
  isLoading = false,
  label,
  name,
  onBlur,
  onChange,
  options,
  selected,
}: Readonly<ISelectDropdownProps>) {
  const handleOnChange = (value: ISelectDropdownOption) => {
    onChange(name, value);
  };

  const handleOnBlur = () => {
    if (onBlur) onBlur(name, selected);
  };

  return (
    <div
      className={clsx('text-input-container', error ? 'input-has-error' : '')}
    >
      <motion.div
        animate={isLabelEnabled ? 'visible' : 'hidden'}
        className={clsx(isLabelEnabled ? 'h-auto' : 'h-0')}
        transition={{ duration: 0.5 }}
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0 },
        }}
      >
        {isLabelEnabled ? <label htmlFor={name}>{label}</label> : null}
      </motion.div>
      <Listbox
        as="div"
        className="
          relative data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60
        "
        disabled={disabled || isLoading}
        name={name}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        value={selected}
      >
        <ListboxButton
          className={clsx(
            'min-w-36 mt-1 flex w-full items-center justify-center gap-2',
            'border bg-white px-3 py-2.5 text-sm font-light',
            'rounded-lg text-gray-500 shadow-none ',
            'rtl:flex-row-reverse',
            error
              ? 'border-red-500/50 text-red-500/50'
              : 'border-gray-200 focus:border-gray-300'
          )}
        >
          <div className="grow text-start">{selected?.label}</div>
          {isLoading ? (
            <ButtonLoader className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="w-5" />
          )}
        </ListboxButton>
        <ListboxOptions
          className="
            absolute z-20 max-h-48 w-full overflow-y-auto shadow
          "
        >
          {options.map((item) => (
            <ListboxOption
              key={item.label}
              className="
                data-focus:bg-gray-100 max-h-36 cursor-pointer bg-white px-5 py-4 text-sm
                shadow-none first:hidden focus:border-gray-300
              "
              value={item}
            >
              {item.label}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
      <div className="input-error-msg">
        {error ? <span>{error}</span> : null}
      </div>
    </div>
  );
}
